import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  School,
  Users,
  Award,
  BookOpen,
  Heart,
  Target,
  Lightbulb,
  CheckCircle,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Send
} from 'lucide-react';

const NewLandingPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'İlkadım360 Hub | Erken Çocukluk Eğitimi Platformu';
  }, []);

  const [formData, setFormData] = useState({
    schoolName: '',
    city: '',
    district: '',
    schoolType: '',
    studentCount: '',
    contactName: '',
    contactRole: '',
    email: '',
    phone: '',
    preferredContact: 'email',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      // Basit form validasyonu
      if (!formData.schoolName || !formData.contactName || !formData.email || !formData.phone) {
        setFormStatus({
          type: 'error',
          message: 'Lütfen zorunlu alanları doldurun.'
        });
        setIsSubmitting(false);
        return;
      }

      // Backend'e başvuru gönder
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accreditation/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Başvuru gönderilemedi');
      }

      // Başarı mesajı
      setFormStatus({
        type: 'success',
        message: 'Başvurunuz başarıyla alınmıştır! YÖMEV ekibimiz en kısa sürede sizinle iletişime geçecektir.'
      });
      
      // Formu temizle
      setFormData({
        schoolName: '',
        city: '',
        district: '',
        schoolType: '',
        studentCount: '',
        contactName: '',
        contactRole: '',
        email: '',
        phone: '',
        preferredContact: 'email',
        message: ''
      });
    } catch (error) {
      console.error('Başvuru hatası:', error);
      setFormStatus({
        type: 'error',
        message: 'Başvuru gönderilirken bir hata oluştu. Lütfen daha sonra tekrar deneyin veya doğrudan bilgi@yomev.org adresine e-posta gönderin.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img src="/logos/ilkadim360-color.jpeg" alt="İlkadım360" className="h-10" />
            </div>
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('program')} className="text-gray-700 hover:text-teal-600 transition">
                Program
              </button>
              <button onClick={() => scrollToSection('akreditasyon')} className="text-gray-700 hover:text-teal-600 transition">
                Akreditasyon Süreci
              </button>
              <button onClick={() => scrollToSection('neden')} className="text-gray-700 hover:text-teal-600 transition">
                Neden İlkadım360?
              </button>
              <button onClick={() => scrollToSection('basvuru')} className="text-gray-700 hover:text-teal-600 transition">
                Başvuru
              </button>
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-teal-600 flex items-center gap-1 transition"
              >
                Akredite okullar girişi →
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Erken Çocukluk ve İlkokulda Kaliteli Eğitimin Güvencesi: İlkadım360
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
              0–10 yaş dönemini kapsayan, oyun temelli ve bütüncül bir eğitim programı. Okulunuza pedagojik altyapı, öğretmen desteği ve kalite güvencesi sunan akreditasyon yaklaşımıyla çocukların gelişimine değer katın.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => scrollToSection('basvuru')}
                className="px-8 py-4 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition shadow-lg hover:shadow-xl"
              >
                Akreditasyon Başvurusu Yap
              </button>
              <button
                onClick={() => scrollToSection('program')}
                className="px-8 py-4 bg-white text-teal-600 border-2 border-teal-600 rounded-lg font-semibold hover:bg-teal-50 transition"
              >
                Programı İncele
              </button>
            </div>
            <div className="mt-8">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-gray-600 hover:text-teal-600 text-sm transition"
              >
                Akredite okullar girişi →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* İLKADIM360 NEDİR */}
      <section id="program" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              İlkadım360 Nedir?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              İlkadım360, erken çocukluk ve ilkokul dönemini kapsayan, çocuğun bütüncül gelişimine odaklanan bir eğitim programıdır. 36 haftalık yapılandırılmış içerikleri, oyun temelli öğrenme yaklaşımı ve aile katılımını destekleyen sistemleriyle okulunuza profesyonel bir altyapı sunar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Heart className="text-teal-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Bütüncül Gelişim Yaklaşımı</h3>
                <p className="text-gray-600">
                  Bilişsel, sosyal-duygusal, fiziksel ve dil gelişimi alanlarını dengeli bir şekilde ele alır. Her çocuğun bireysel gelişim sürecine saygı gösterir.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Users className="text-emerald-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Oyun Temelli Öğrenme</h3>
                <p className="text-gray-600">
                  Çocukların doğal öğrenme biçimi olan oyunu merkeze alır. Keşfetme, deneme ve yaratıcılıkla öğrenmeyi destekler.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="text-blue-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Yapılandırılmış Program</h3>
                <p className="text-gray-600">
                  36 haftalık detaylı planlama ile öğretmenlere hazır, uygulanabilir içerikler sunar. Esneklik ve uyarlanabilirlik ilkesiyle her sınıfın ihtiyacına cevap verir.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Target className="text-purple-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Yönelim → Uygulama → Farkındalık → Paylaşım</h3>
                <p className="text-gray-600">
                  Her hafta bu dört aşamada ilerleyen öğrenme döngüsü, çocukların konuları derinlemesine kavramasını sağlar.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEDEN İLKADIM360? */}
      <section id="neden" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Neden Okulunuzda İlkadım360 Olmalı?
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Eğitimciler ve Yöneticiler İçin */}
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <School className="text-teal-600" size={28} />
                Eğitimciler ve Yöneticiler İçin
              </h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Kalite Güvencesi ve Akreditasyon</h4>
                    <p className="text-gray-600 text-sm">
                      İlkadım360 akreditasyonu, okulunuzun eğitim kalitesini ulusal standartlarda garanti eder. Velilere ve topluma güven veren bir etiket sağlar.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Hazır Program ve Profesyonel Destek</h4>
                    <p className="text-gray-600 text-sm">
                      Haftalık planlar, etkinlik önerileri ve değerlendirme araçları ile öğretmenlerinizin iş yükünü organize eder. Sürekli gelişim için online eğitimler ve rehberlik sunar.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Öğretmen Gelişim Ekosistemi</h4>
                    <p className="text-gray-600 text-sm">
                      Canlı eğitimler, doküman kütüphanesi ve akran öğrenmesi fırsatlarıyla öğretmenleriniz mesleki olarak güçlenir.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Şeffaf ve Ölçülebilir Süreç</h4>
                    <p className="text-gray-600 text-sm">
                      Akreditasyon süreci, okulunuzun güçlü yönlerini ve gelişim alanlarını net bir şekilde ortaya koyar. Sürekli iyileşme odaklı bir yaklaşım benimser.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ebeveynler İçin */}
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Heart className="text-rose-600" size={28} />
                Ebeveynler İçin
              </h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <CheckCircle className="text-rose-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Çocuğun Bütüncül Gelişimi</h4>
                    <p className="text-gray-600 text-sm">
                      Sadece akademik başarı değil, duygusal, sosyal ve fiziksel gelişim de programa entegredir. Çocuğunuz dengeli büyür.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="text-rose-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Güvenilir ve Denetlenen Okul</h4>
                    <p className="text-gray-600 text-sm">
                      İlkadım360 akreditasyonuna sahip okullar, düzenli değerlendirme ve izleme süreçlerinden geçer. Okulunuzun kalitesine güvenle bakabilirsiniz.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="text-rose-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Aile Katılımı ve Şeffaflık</h4>
                    <p className="text-gray-600 text-sm">
                      Program, aile-okul iş birliğini teşvik eder. Çocuğunuzun gelişim sürecini takip edebilir, sürece dahil olabilirsiniz.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="text-rose-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Oyun ve Keşif Odaklı Öğrenme</h4>
                    <p className="text-gray-600 text-sm">
                      Çocuğunuz sıkılmadan, baskı altında kalmadan öğrenir. Merak ve keşif duygusu korunarak eğitim alır.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AKREDİTASYON SÜRECİ */}
      <section id="akreditasyon" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nasıl İş Birliği Yapıyoruz?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              İlkadım360 akreditasyon süreci, okulunuzu cezalandırmak için değil, desteklemek ve geliştirmek için tasarlanmıştır. Şeffaf, rehberlik odaklı ve sürekli gelişim ilkesiyle ilerler.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Adım 1 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  1
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Başvuru ve İlk Değerlendirme
                </h3>
                <p className="text-sm text-gray-600">
                  Okulunuzdan başvuru formu alınır. YÖMEV ekibi, okulunuzun mevcut durumunu ve ihtiyaçlarını değerlendirir. Ön görüşme ve bilgilendirme yapılır.
                </p>
              </div>
            </div>

            {/* Adım 2 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6">
                <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  2
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Hazırlık ve Eğitim Dönemi
                </h3>
                <p className="text-sm text-gray-600">
                  Öğretmen ve yöneticilere yönelik temel eğitimler düzenlenir. İlkadım360 programının içeriği, felsefesi ve uygulanması aktarılır. Okulunuz programa uyum sağlamaya başlar.
                </p>
              </div>
            </div>

            {/* Adım 3 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  3
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Uygulama ve İzleme
                </h3>
                <p className="text-sm text-gray-600">
                  Okulunuz İlkadım360 programını uygularken YÖMEV ekibi düzenli ziyaretler ve online destek ile süreci izler. Geri bildirimler ve rehberlik sağlanır.
                </p>
              </div>
            </div>

            {/* Adım 4 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6">
                <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  4
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Akreditasyon ve Sürekli Gelişim
                </h3>
                <p className="text-sm text-gray-600">
                  Belirlenen kriterleri karşılayan okullar İlkadım360 akreditasyonu alır. Süreç burada bitmez; düzenli yıllık değerlendirmeler ve profesyonel gelişim desteği devam eder.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMDAN ÖNE ÇIKANLAR */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Programdan Öne Çıkanlar
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <Lightbulb className="text-teal-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Oyun Temelli Sınıf Ortamı</h3>
              <p className="text-gray-600">
                İlkadım360 sınıfları, çocukların özgürce keşfedebileceği, deneyebileceği ve yaratıcılıklarını geliştirebileceği öğrenme alanlarıdır. Materyaller ve düzenlemeler, oyun yoluyla öğrenmeyi destekler.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <Heart className="text-emerald-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Aile ile Güçlü İş Birliği</h3>
              <p className="text-gray-600">
                Program, aileleri sürece dahil eden yapısıyla öne çıkar. Veli iletişim sistemleri, ev uygulamaları ve aile katılım etkinlikleri ile okul-ev bağı güçlenir.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="text-blue-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Öğretmen Gelişim ve Destek Sistemi</h3>
              <p className="text-gray-600">
                Öğretmenler, online eğitim platformu, canlı atölye çalışmaları ve doküman kütüphanesi ile sürekli profesyonel destek alır. Akran öğrenmesi ve mentorluk sistemleri mevcuttur.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <BookOpen className="text-purple-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Dijital Kaynaklar ve İlkadım360 Hub</h3>
              <p className="text-gray-600">
                Akredite okullar, İlkadım360 Hub platformu üzerinden haftalık planlara, etkinlik önerilerine, değerlendirme araçlarına ve topluluk forumlarına erişir. Tüm kaynaklar güncel ve kolay erişilebilir.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* YÖM EĞİTİM VAKFI HAKKINDA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              YÖM Eğitim Vakfı Hakkında
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              YÖM Eğitim Vakfı (YÖMEV), Türkiye'de erken çocukluk ve ilkokul eğitiminde kaliteyi yükseltmeyi, eğitimde fırsat eşitliğini desteklemeyi ve öğretmen ekosistemini güçlendirmeyi amaçlayan bir sivil toplum kuruluşudur. İlkadım360 programı, YÖMEV'in 15 yıllık deneyimi ve uzmanlığının ürünüdür.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-teal-600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Eğitimde Kalite ve Erişilebilirlik</h3>
              <p className="text-sm text-gray-600">
                Her çocuğun kaliteli eğitime erişmesi için çalışır. Programlar, bilimsel temellere dayanır ve sürekli güncellenir.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-emerald-600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Okul ve Öğretmen Odaklı Yaklaşım</h3>
              <p className="text-sm text-gray-600">
                Okulları ve öğretmenleri yalnız bırakmaz. Rehberlik, eğitim ve sürekli destek sağlar. İş birliği ve güven ilişkisi kurar.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="text-blue-600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Uzun Vadeli Vizyon ve Sosyal Etki</h3>
              <p className="text-sm text-gray-600">
                Sadece bugünü değil, gelecek nesillerin eğitim kalitesini düşünür. Sürdürülebilir, ölçülebilir ve toplumsal fayda yaratan projeler geliştirir.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BAŞVURU & İLETİŞİM */}
      <section id="basvuru" className="py-20 bg-gradient-to-br from-teal-600 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Okulunuzda İlkadım360'ı Başlatın
            </h2>
            <p className="text-lg text-teal-50">
              Okulunuzda İlkadım360 programını uygulamak ve akreditasyon sürecini başlatmak için formu doldurun. YÖMEV ekibi sizinle en kısa sürede iletişime geçecektir. Birlikte, çocuklarımıza daha kaliteli bir eğitim sunalım.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Okul Bilgileri */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Okul Bilgileri</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Okul Adı *
                    </label>
                    <input
                      type="text"
                      name="schoolName"
                      value={formData.schoolName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Örn: Çağdaş Eğitim Kurumları"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">İl</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      placeholder="İstanbul"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">İlçe</label>
                    <input
                      type="text"
                      name="district"
                      value={formData.district}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      placeholder="Kadıköy"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Okul Türü</label>
                    <select
                      name="schoolType"
                      value={formData.schoolType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">Seçiniz</option>
                      <option value="anaokulu">Anaokulu</option>
                      <option value="ilkokul">İlkokul</option>
                      <option value="anaokulu_ilkokul">Anaokulu + İlkokul</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Öğrenci Sayısı</label>
                    <select
                      name="studentCount"
                      value={formData.studentCount}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">Seçiniz</option>
                      <option value="0-50">0-50</option>
                      <option value="51-100">51-100</option>
                      <option value="101-200">101-200</option>
                      <option value="201+">201+</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* İletişim Bilgileri */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">İletişim Bilgileri</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      placeholder="Ahmet Yılmaz"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Görev</label>
                    <input
                      type="text"
                      name="contactRole"
                      value={formData.contactRole}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      placeholder="Kurucu / Müdür / Koordinatör"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      placeholder="ahmet@okul.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      placeholder="0532 XXX XX XX"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tercih edilen iletişim yöntemi
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="email"
                          checked={formData.preferredContact === 'email'}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">E-posta</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="phone"
                          checked={formData.preferredContact === 'phone'}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        <span className="text-gray-700">Telefon</span>
                      </label>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mesaj / Not
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      placeholder="İlkadım360 hakkında öğrenmek istediğiniz konular veya özel notlarınız..."
                    />
                  </div>
                </div>
              </div>

              {/* Durum Mesajı */}
              {formStatus.message && (
                <div
                  className={`p-4 rounded-lg ${
                    formStatus.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {formStatus.message}
                </div>
              )}

              {/* Submit Butonu */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition shadow-lg hover:shadow-xl"
                >
                  {isSubmitting ? (
                    <>İşleniyor...</>
                  ) : (
                    <>
                      <Send size={20} />
                      Başvuruyu Gönder
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Column 1 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/logos/ilkadim360-color.jpeg" alt="İlkadım360" className="h-12" />
                <img src="/logos/yomev-logo.jpg" alt="YÖMEV" className="h-8" />
              </div>
              <p className="text-sm text-gray-400">
                YÖM Eğitim Vakfı tarafından geliştirilen, erken çocukluk ve ilkokul dönemini kapsayan bütüncül eğitim programı ve akreditasyon yaklaşımı.
              </p>
            </div>

            {/* Column 2 */}
            <div>
              <h3 className="text-white font-semibold mb-4">İletişim</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-teal-500" />
                  <a href="mailto:bilgi@yomev.org" className="hover:text-teal-500 transition">
                    bilgi@yomev.org
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={16} className="text-teal-500" />
                  <a href="tel:+905409096638" className="hover:text-teal-500 transition">
                    +90 540 909 6638
                  </a>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin size={16} className="text-teal-500 flex-shrink-0 mt-1" />
                  <span>İstanbul, Türkiye</span>
                </div>
              </div>
            </div>

            {/* Column 3 */}
            <div>
              <h3 className="text-white font-semibold mb-4">Bağlantılar</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <button
                    onClick={() => scrollToSection('program')}
                    className="hover:text-teal-500 transition"
                  >
                    İlkadım360 Programı
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('akreditasyon')}
                    className="hover:text-teal-500 transition"
                  >
                    Akreditasyon Süreci
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('basvuru')}
                    className="hover:text-teal-500 transition"
                  >
                    Başvuru Yap
                  </button>
                </li>
                <li>
                  <Link to="/login" className="hover:text-teal-500 transition">
                    Akredite Okullar Girişi
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-500">
            <p>© 2025 YÖM Eğitim Vakfı - İlkadım360. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewLandingPage;
